import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CartContext } from '../contexts/CartContext';
import { apiRequest, apiPost } from '../utils/fordApi';
import { pushViewerEvent } from '../utils/viewerDataLayer';
import CategoryList from './ThreeDViewer/CategoryList';
import AccessoryList from './ThreeDViewer/AccessoryList';
import { safeRichTextHtml } from '../utils/safeRichText';
import { findPerformanceCategoryId } from '../utils/racingCategory';

function ThreeDViewer({ accessoryId, onClose, initialModelId = null, initialVariantId = null, initialYearId = null, entrySource: entrySourceProp = null }) {
    const { cart, addToCart, removeFromCart } = useContext(CartContext);
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [config, setConfig] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [availableAccessories, setAvailableAccessories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cartCountsByCategory, setCartCountsByCategory] = useState({ counts: {}, total: 0 });
    const [selectedCategory, setSelectedCategory] = useState({ id: null, name: 'All Categories' }); // null = category list; { id, name } = accessory list (id null = all accessories)
    const [show3dOnlyInViewer, setShow3dOnlyInViewer] = useState(false);
    const [featureHpEnabled, setFeatureHpEnabled] = useState(true);
    const [currentView, setCurrentView] = useState('exterior');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [compatibilityModal, setCompatibilityModal] = useState(null); // { type: 'conflict'|'dependency'|'replacement', accessory, conflicts/dependencies/replacements, onConfirm, onCancel }
    const [accessoryDetailFlyout, setAccessoryDetailFlyout] = useState(null); // { accessory: full accessory object, loading: boolean }
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [isUpdatingAccessories, setIsUpdatingAccessories] = useState(false); // Track when accessories are being added/removed
    const [accessoriesLoading, setAccessoriesLoading] = useState(false); // Track when accessories list is loading (e.g. category change)
    const [loadingProgress, setLoadingProgress] = useState(0); // Track 3D model loading progress (0-100)
    const [isFadingOut, setIsFadingOut] = useState(false); // Track fade-out animation state
    const [preloadingCartAccessories, setPreloadingCartAccessories] = useState(false); // Hide 3D until cart items are on the car
    const [preloadProgress, setPreloadProgress] = useState(0); // Progress for accessories preload overlay (0-100)
    const [inAccessoriesView, setInAccessoriesView] = useState(false); // Tracks when ONE3D is in accessories view (for 360 button enablement)
    
    const one3dContainerRef = useRef(null);
    const viewerSectionRef = useRef(null);
    const one3dInstanceRef = useRef(null);
    const scriptRef = useRef(null);
    const isInitializingRef = useRef(false);
    const hasInitializedRef = useRef(false);
    const isLoadingConfigRef = useRef(false);
    const isCleaningUpRef = useRef(false);
    const resizeObserverRef = useRef(null);
    const resizeHandlerRef = useRef(null);
    const isMountedRef = useRef(true);
    const lastProgressRef = useRef(0);
    const lastProgressUpdateRef = useRef(0);
    const hasPreloadedFromCartRef = useRef(false);
    const cartRef = useRef(null);
    const availableAccessoriesRef = useRef([]);
    const appliedEntryCategoryRef = useRef(false);
    const viewerSessionStartRef = useRef(null);
    const viewerOpenedEmittedRef = useRef(false);
    const one3dLoadStartRef = useRef(null);
    const accessoriesAddedCountSessionRef = useRef(0);
    const accessoriesRemovedCountSessionRef = useRef(0);
    const prevFullscreenRef = useRef(false);
    const cameraInteractionEmittedRef = useRef(false);
    const firstModelVariantEmittedRef = useRef(false);
    const initialModelVariantSourceEmittedRef = useRef(false);
    const categoryListViewEmittedRef = useRef(false);
    const previousModelIdRef = useRef(null);
    const previousVariantIdRef = useRef(null);
    const viewerSessionEndedRef = useRef(false);
    const detailAccessoryIdRef = useRef(null);

    const resolvedEntrySource = entrySourceProp || (initialModelId || initialVariantId ? 'deeplink' : 'accessory_detail');

    const accessoryAnalyticsPayload = (acc) => ({
        accessory_id: acc?.id,
        part_number: acc?.part_number || undefined,
    });

    useEffect(() => {
        cartRef.current = cart;
        availableAccessoriesRef.current = availableAccessories;
    }, [cart, availableAccessories]);

    // Performance / Ford Racing category id (for showing racing icon on cards when this category is selected)
    const racingCategoryId = useMemo(
        () => findPerformanceCategoryId(categories),
        [categories]
    );

    useEffect(() => {
        isMountedRef.current = true;
        hasInitializedRef.current = false;
        hasPreloadedFromCartRef.current = false;
        isInitializingRef.current = false;
        isLoadingConfigRef.current = false;
        setLoadingProgress(0);
        
        teardownCanvas().catch(() => {});
        load3dConfig();
        
        return () => {
            isMountedRef.current = false;
            teardownCanvas().catch(() => {});
            isLoadingConfigRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessoryId]);

    useEffect(() => {
        return () => {
            if (viewerOpenedEmittedRef.current && !viewerSessionEndedRef.current) {
                viewerSessionEndedRef.current = true;
                const durationSec = viewerSessionStartRef.current
                    ? Math.round((Date.now() - viewerSessionStartRef.current) / 1000)
                    : undefined;
                pushViewerEvent('viewer_session_timed', {
                    session_duration_seconds: durationSec,
                    accessories_added_count: accessoriesAddedCountSessionRef.current,
                    accessories_removed_count: accessoriesRemovedCountSessionRef.current,
                });
                pushViewerEvent('viewer_closed', { session_duration_seconds: durationSec });
            }
        };
    }, []);

    // Browser back button - tear down 3D canvas before navigation
    useEffect(() => {
        const handlePopState = () => {
            teardownCanvas().catch(() => {});
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Only initialize once - prevent double loading from StrictMode
        if (config && selectedModel && selectedVariant && !hasInitializedRef.current && !isInitializingRef.current) {
            // Check if container exists and doesn't have canvas
            if (one3dContainerRef.current) {
                const existingCanvas = one3dContainerRef.current.querySelector('canvas');
                if (existingCanvas) {
                    console.log('⚠️ Canvas already exists in useEffect, skipping');
                    return;
                }
            }
            
            // Set flag immediately to prevent double calls
            isInitializingRef.current = true;
            
            // No delay - align with Sample-prod.html which calls init immediately when script is ready
            loadOne3D();
        }
    }, [config, selectedModel, selectedVariant]);

    useEffect(() => {
        if (selectedModel && selectedVariant) {
            loadCategoriesForModelVariant();
        }
    }, [selectedModel, selectedVariant]);

    useEffect(() => {
        if (selectedModel && selectedVariant) {
            loadCartCountsByCategory();
        }
    }, [selectedModel, selectedVariant, cart]);

    useEffect(() => {
        if (selectedModel && selectedVariant) {
            loadAccessoriesForModelVariant();
        }
    }, [selectedModel, selectedVariant, selectedCategory]);

    // When opened from detail page (accessoryId set), auto-select the entry accessory's category once (do not overwrite if user later clicks "All Categories")
    const entryCategoryId = config?.current_accessory?.category_id ?? null;
    useEffect(() => {
        try {
            if (appliedEntryCategoryRef.current) return;
            if (!accessoryId || entryCategoryId == null || !Array.isArray(categories) || categories.length === 0) return;
            const cat = categories.find(c => c && (c.id === entryCategoryId || c.id === Number(entryCategoryId)));
            if (cat) {
                setSelectedCategory(cat);
                appliedEntryCategoryRef.current = true;
            }
        } catch (e) {
            console.warn('3D viewer: auto-select category failed', e);
        }
    }, [accessoryId, entryCategoryId, categories]);

    useEffect(() => {
        if (selectedCategory != null) categoryListViewEmittedRef.current = false;
    }, [selectedCategory]);

    useEffect(() => {
        if (selectedCategory != null || categories.length === 0) return;
        if (categoryListViewEmittedRef.current) return;
        categoryListViewEmittedRef.current = true;
        pushViewerEvent('viewer_category_list_viewed');
    }, [selectedCategory, categories]);

    useEffect(() => {
        if (process.env.REACT_APP_BRAND_ID !== '1' || !show3dOnlyInViewer) return;
        if (!selectedCategory?.id) return;
        if (availableAccessories.filter((a) => a.one3d_item_code).length !== 0) return;
        pushViewerEvent('viewer_category_empty_state_viewed', { category_id: selectedCategory.id });
    }, [show3dOnlyInViewer, selectedCategory, availableAccessories]);

    useEffect(() => {
        if (!compatibilityModal) return;
        const cc =
            compatibilityModal.conflicts?.length ??
            compatibilityModal.dependencies?.length ??
            compatibilityModal.replacements?.length ??
            0;
        pushViewerEvent('viewer_compatibility_modal_shown', {
            modal_type: compatibilityModal.type,
            accessory_id: compatibilityModal.accessory?.id,
            conflicts_count: cc,
        });
    }, [compatibilityModal]);

    useEffect(() => {
        // Update accessories in One3D when selection changes (after initial load)
        if (!loading && selectedAccessories.length > 0 && typeof window.ONE3D !== 'undefined') {
            // This will be handled by the preConfig in initializeOne3D
        }
    }, [selectedAccessories, loading]);

    // Setup canvas resize observer for responsive canvas
    useEffect(() => {
        if (!one3dContainerRef.current || !hasInitializedRef.current) return;

        const safeResize = () => {
            if (isCleaningUpRef.current) return;
            const container = one3dContainerRef.current;
            if (!container || !container.isConnected) return;
            const canvas = container.querySelector('canvas');
            if (!canvas || !canvas.isConnected) return;
            if (typeof window.ONE3D?.resize !== 'function') return;
            try {
                window.ONE3D.resize();
            } catch (err) {
                const msg = err?.message ?? '';
                if (!msg.includes("reading 'y'") && !msg.includes('getClientRects')) {
                    console.warn('One3D resize error:', err);
                }
            }
        };

        const resizeObserver = new ResizeObserver(safeResize);
        resizeObserverRef.current = resizeObserver;
        resizeHandlerRef.current = safeResize;
        resizeObserver.observe(one3dContainerRef.current);

        const handleWindowResize = safeResize;
        window.addEventListener('resize', handleWindowResize);

        return () => {
            resizeObserverRef.current = null;
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [loading]); // Re-run when loading changes (One3D initialized)

    const load3dConfig = async () => {
        // Prevent multiple simultaneous config loads
        if (isLoadingConfigRef.current) {
            console.log('⚠️ Config already loading, skipping');
            return;
        }

        try {
            isLoadingConfigRef.current = true;
            setLoading(true);
            setError(null);

            const config_response = await apiRequest(`/api/v1/accessories/${accessoryId}/3d-config`);

            if (!isMountedRef.current) return;

            if (!config_response.available) {
                pushViewerEvent('viewer_3d_load_error', { error_type: 'config_unavailable', model_id: undefined, variant_id: undefined });
                setError('3D configuration not available for this accessory');
                setLoading(false);
                isLoadingConfigRef.current = false;
                return;
            }

            setConfig(config_response);
            console.log('📍 3D Config CDN URL:', config_response.cdn_url);

            // Use initial model/variant from URL params if provided, otherwise use defaults
            let modelToSelect = config_response.default_model;
            let variantToSelect = config_response.default_variant;
            
            if (initialModelId && config_response.models) {
                const foundModel = config_response.models.find(m => m.id === initialModelId);
                if (foundModel) {
                    modelToSelect = foundModel;
                    // Find variant for this model
                    const variantsByModel = config_response.variants_by_model || {};
                    const variants = variantsByModel[initialModelId] || [];
                    if (initialVariantId) {
                        const foundVariant = variants.find(v => v.id === initialVariantId);
                        if (foundVariant) {
                            variantToSelect = foundVariant;
                        } else if (variants.length > 0) {
                            variantToSelect = variants[0];
                        }
                    } else if (variants.length > 0) {
                        variantToSelect = variants[0];
                    }
                }
            }
            
            setSelectedModel(modelToSelect);
            setSelectedVariant(variantToSelect);
            
            // Pre-select the entry accessory in the 3D overlay (from detail page) so user doesn't have to search again.
            // This only marks it as selected in state; we add it to the car after One3D init in a small effect.
            const entryCode = config_response.current_accessory?.one3d_item_code;
            if (entryCode && typeof entryCode === 'string') {
                setSelectedAccessories(prev => {
                    const list = Array.isArray(prev) ? prev : [];
                    if (list.includes(entryCode)) return list;
                    return [entryCode, ...list];
                });
            }
            
            isLoadingConfigRef.current = false;
        } catch (err) {
            if (!isMountedRef.current) return;
            console.error('Error loading 3D config:', err);
            pushViewerEvent('viewer_3d_load_error', { error_type: 'config_request_failed', model_id: undefined, variant_id: undefined });
            setError(err.response?.data?.message || err?.message || 'Failed to load 3D configuration');
            setLoading(false);
            isLoadingConfigRef.current = false;
        }
    };

    const loadCategoriesForModelVariant = async () => {
        if (!selectedModel || !selectedVariant) return;

        try {
            const data = await apiRequest('/api/v1/accessories/model-variant/categories', {
                model_id: selectedModel.id,
                variant_id: selectedVariant.id
            });
            const categoriesList = data.categories || [];
            setCategories(categoriesList);
            // Keep category unselected - user chooses from list
        } catch (err) {
            console.error('Error loading categories for model/variant:', err);
            setCategories([]);
        }
    };

    const loadCartCountsByCategory = async () => {
        if (!selectedModel || !selectedVariant) return;
        const items = Object.values(cart || {}).map(({ id, quantity }) => ({
            accessory_id: id,
            quantity: quantity || 0
        }));
        if (items.length === 0) {
            setCartCountsByCategory({ counts: {}, total: 0 });
            return;
        }
        try {
            const data = await apiPost('/api/v1/accessories/model-variant/cart-counts-by-category', {
                model_id: selectedModel.id,
                variant_id: selectedVariant.id,
                items
            });
            setCartCountsByCategory({
                counts: data.counts || {},
                total: data.total ?? 0
            });
        } catch (err) {
            console.error('Error loading cart counts by category:', err);
            setCartCountsByCategory({ counts: {}, total: 0 });
        }
    };

    const loadAccessoriesForModelVariant = async () => {
        if (!selectedModel || !selectedVariant) return;
        if (!selectedCategory) return; // Don't load when showing category list

        setAccessoriesLoading(true);
        try {
            const params = {
                model_id: selectedModel.id,
                variant_id: selectedVariant.id
            };
            if (selectedCategory.id) {
                params.category_id = selectedCategory.id;
            }
            const data = await apiRequest('/api/v1/accessories/model-variant', params);

            // Filter out duplicates by ID
            const accessories = data.accessories || [];
            const uniqueAccessories = accessories.filter((accessory, index, self) => 
                index === self.findIndex(a => a.id === accessory.id)
            );
            // When opened from detail page (accessoryId set), put the entry accessory at the top of the list
            let sorted = uniqueAccessories;
            if (accessoryId != null && accessoryId !== '' && !Number.isNaN(Number(accessoryId)) && Array.isArray(uniqueAccessories)) {
                const id = Number(accessoryId);
                sorted = [...uniqueAccessories].sort((a, b) => (Number(a?.id) === id ? -1 : Number(b?.id) === id ? 1 : 0));
            }
            setAvailableAccessories(sorted);
        } catch (err) {
            console.error('Error loading accessories for model/variant:', err);
            setAvailableAccessories([]);
        } finally {
            setAccessoriesLoading(false);
        }
    };

    // Async teardown - MUST await destroy before clearing container. Captures refs at start so it works on popstate (back) when component unmounts.
    const teardownCanvas = () => {
        isCleaningUpRef.current = true;
        isInitializingRef.current = false;
        hasInitializedRef.current = false;

        const containerEl = one3dContainerRef.current;
        const instanceRef = one3dInstanceRef.current;
        one3dInstanceRef.current = null;

        if (resizeObserverRef.current) {
            try { resizeObserverRef.current.disconnect(); } catch (e) { /* ignore */ }
            resizeObserverRef.current = null;
        }
        if (resizeHandlerRef.current) {
            window.removeEventListener('resize', resizeHandlerRef.current);
            resizeHandlerRef.current = null;
        }

        const destroyPromise = (() => {
            if (!instanceRef) return Promise.resolve();
            if (instanceRef instanceof Promise) {
                return instanceRef
                    .then((instance) => {
                        if (instance && typeof instance.destroy === 'function') {
                            try { instance.destroy(); } catch (e) { /* ignore */ }
                        }
                    })
                    .catch(() => {});
            }
            if (typeof instanceRef.destroy === 'function') {
                try { instanceRef.destroy(); } catch (e) { /* ignore */ }
            }
            return Promise.resolve();
        })();

        return destroyPromise
            .then(() => {
                if (typeof window.ONE3D !== 'undefined' && typeof window.ONE3D.destroy === 'function') {
                    try { window.ONE3D.destroy(); } catch (e) { /* ignore */ }
                    // Stub ONE3D so any lingering RAF/callbacks (e.g. reading .y) don't run library code
                    const noop = () => {};
                    const noopPromise = () => Promise.resolve();
                    window.ONE3D = {
                        destroy: noop,
                        resize: noop,
                        init: noopPromise,
                        addAccessory: noopPromise,
                        removeAccessory: noopPromise,
                        setAccessorieCamera: noopPromise,
                        resetView: noop,
                        toggleFeatureHp: noop,
                        exteriorView: noop,
                        interiorView: noop,
                    };
                }
            })
            .then(() => new Promise((r) => setTimeout(r, 150)))
            .then(() => {
                if (containerEl) {
                    try {
                        containerEl.querySelectorAll('canvas').forEach((c) => { try { c.remove(); } catch (e) {} });
                        document.querySelectorAll('canvas[id="one3d-canvas"], canvas[id="one3d"]').forEach((c) => {
                            try { if (c.parentNode) c.remove(); } catch (e) {}
                        });
                        while (containerEl.firstChild) containerEl.removeChild(containerEl.firstChild);
                        containerEl.innerHTML = '';
                    } catch (e) { /* ignore */ }
                }
            })
            .then(() => new Promise((r) => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            isCleaningUpRef.current = false;
                            r();
                        }, 400);
                    });
                });
            }))
            .then(() => {
                // Restore scroll on body/html (One3D or viewer may set overflow: hidden)
                try {
                    document.body.style.overflow = '';
                    document.body.style.height = '';
                    document.body.style.position = '';
                    document.documentElement.style.overflow = '';
                    document.documentElement.style.height = '';
                } catch (e) { /* ignore */ }
            });
    };

    // Single function: tear down 3D canvas and optionally navigate (close). Used by close button and browser back.
    const teardownAndClose = (shouldNavigate = true) => {
        window.onbeforeunload = null;
        const isOne3dTeardownError = (msg) =>
            (msg && (msg.includes("reading 'y'") || msg.includes('getClientRects') || msg.includes('setAnimationLoop')));
        const suppressOne3dError = (e) => {
            const msg = e?.message ?? e?.error?.message ?? '';
            if (isOne3dTeardownError(msg)) {
                e.preventDefault?.();
                e.stopPropagation?.();
            }
        };
        const prevOnError = window.onerror;
        window.addEventListener('error', suppressOne3dError, { capture: true });
        window.onerror = (msg, ...rest) => (isOne3dTeardownError(msg) ? true : (prevOnError ? prevOnError(msg, ...rest) : false));

        const cleanup = () => {
            window.removeEventListener('error', suppressOne3dError, { capture: true });
            window.onerror = prevOnError;
        };

        return teardownCanvas().then(() => {
            if (!viewerSessionEndedRef.current) {
                viewerSessionEndedRef.current = true;
                const durationSec = viewerSessionStartRef.current
                    ? Math.round((Date.now() - viewerSessionStartRef.current) / 1000)
                    : undefined;
                pushViewerEvent('viewer_session_timed', {
                    session_duration_seconds: durationSec,
                    accessories_added_count: accessoriesAddedCountSessionRef.current,
                    accessories_removed_count: accessoriesRemovedCountSessionRef.current,
                });
                pushViewerEvent('viewer_closed', { session_duration_seconds: durationSec });
            }
            if (shouldNavigate && onClose) {
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            onClose();
                            setTimeout(cleanup, 3000);
                        });
                    });
                }, 200);
            } else {
                cleanup();
            }
        });
    };


    const loadOne3D = async () => {
        if (!config || !selectedModel || !selectedVariant) {
            isInitializingRef.current = false;
            return;
        }

        // Wait for container to be available
        if (!one3dContainerRef.current) {
            setTimeout(() => loadOne3D(), 100);
            return;
        }

        const container = one3dContainerRef.current;
        
        // CRITICAL: Check if canvas already exists - prevent double initialization
        const existingCanvas = container.querySelector('canvas');
        if (existingCanvas) {
            console.log('⚠️ Canvas already exists, skipping initialization');
            isInitializingRef.current = false;
            hasInitializedRef.current = true;
            return;
        }

        // Prevent double initialization - check both refs and DOM
        if (hasInitializedRef.current) {
            console.log('⚠️ Already initialized, skipping');
            isInitializingRef.current = false;
            return;
        }

        // If not already initializing, mark it now
        if (!isInitializingRef.current) {
            isInitializingRef.current = true;
        }

        // CRITICAL: Await teardown before loading - prevents .y error (One3D RAF accessing torn-down state)
        await teardownCanvas();
        if (!isMountedRef.current) return;
        isInitializingRef.current = true;

        // Double-check container is empty after cleanup
        if (container.querySelector('canvas')) {
            console.log('⚠️ Container still has canvas after cleanup, clearing again');
            container.innerHTML = '';
        }

        setLoading(true);
        setError(null);
        
        // Clear container - simple like POC
        container.innerHTML = '';
        
        // Ensure container has ID
        container.id = 'one3d';

        // Reuse ONE3D only if init is available (library can be corrupted after destroy)
        if (typeof window.ONE3D !== 'undefined' && typeof window.ONE3D.init === 'function') {
            console.log('♻️ Reusing existing ONE3D library');
            initializeOne3D();
            return;
        }

        // Load script when ONE3D not loaded or corrupted - remove old script first to avoid duplicates
        const existingScript = document.querySelector('script[src*="one3d_functions.min.js"]');
        if (existingScript) existingScript.remove();

        const baseCdn = config.cdn_url.replace(/\/?$/, '');
        const scriptPath = selectedModel.one3d_script_path
            ? `${baseCdn}/${selectedModel.one3d_script_path}`
            : `${baseCdn}/one3d/assets/${selectedModel.one3d_script_folder || selectedModel.one3d_model_id}/one3d_functions.min.js`;
        const script = document.createElement('script');
        script.src = scriptPath;
        script.type = 'text/javascript';
        scriptRef.current = script;
        console.log('📜 Loading One3D script:', scriptPath);

        script.onload = () => {
            if (isCleaningUpRef.current || !isMountedRef.current) return;
            const canvasDuringLoad = container.querySelector('canvas');
            if (canvasDuringLoad) {
                console.log('⚠️ Canvas appeared during script load, clearing before init');
                container.innerHTML = '';
            }
            initializeOne3D();
        };

        script.onerror = () => {
            if (!isMountedRef.current) return;
            isInitializingRef.current = false;
            pushViewerEvent('viewer_script_load_error');
            setError('Failed to load One3D library. Please check your internet connection.');
            setLoading(false);
        };

        document.head.appendChild(script);
    };

    const initializeOne3D = () => {
        try {
            // Final check before initializing - prevent double init
            if (!one3dContainerRef.current) {
                console.error('❌ Container not available');
                isInitializingRef.current = false;
                return;
            }

            const container = one3dContainerRef.current;
            
            // CRITICAL: Check if canvas already exists
            const existingCanvas = container.querySelector('canvas');
            if (existingCanvas) {
                console.log('⚠️ Canvas already exists in container, skipping initialization');
                isInitializingRef.current = false;
                hasInitializedRef.current = true;
                setLoading(false);
                return;
            }

            if (typeof window.ONE3D === 'undefined') {
                isInitializingRef.current = false;
                setError('One3D library not loaded');
                setLoading(false);
                return;
            }

            // Align with Sample-prod.html: minimal config, add preConfig and onProgress (throttled to reduce re-renders)
            lastProgressRef.current = 0;
            lastProgressUpdateRef.current = 0;
            const baseUrl = `${config.cdn_url.replace(/\/?$/, '')}/one3d/`;
            const PROGRESS_THROTTLE_MS = 200;
            const PROGRESS_STEP = 5;
            const one3dConfig = {
                showDefaultLoader: false,
                ...(selectedAccessories.length > 0 && { preConfig: selectedAccessories }),
                onProgress: (e) => {
                    let progress = 0;
                    if (typeof e === 'number') {
                        progress = e;
                    } else if (typeof e === 'string') {
                        progress = parseFloat(e) || 0;
                    } else if (e && typeof e === 'object') {
                        progress = e.progress ?? e.percent ?? e.percentage ?? e.loaded ?? e.value ?? 0;
                        if (progress > 0 && progress <= 1) progress *= 100;
                    }
                    progress = Math.min(100, Math.max(0, progress));
                    const now = Date.now();
                    const stepOk = progress - lastProgressRef.current >= PROGRESS_STEP;
                    const timeOk = now - lastProgressUpdateRef.current >= PROGRESS_THROTTLE_MS;
                    const isComplete = progress >= 100;
                    if (isMountedRef.current && (stepOk || timeOk || isComplete)) {
                        lastProgressRef.current = progress;
                        lastProgressUpdateRef.current = now;
                        setLoadingProgress(progress);
                    }
                }
            };
            console.log('🔧 One3D init:', { containerId: 'one3d', baseUrl, modelId: selectedModel.one3d_model_id, variantId: selectedVariant.one3d_variant_id, config: one3dConfig });

            one3dLoadStartRef.current = Date.now();

            // Minimal init like Sample-prod.html - ONE3D.init(containerId, baseUrl, modelId, variantId, config)
            const initPromise = window.ONE3D.init(
                'one3d',
                baseUrl,
                selectedModel.one3d_model_id,
                selectedVariant.one3d_variant_id,
                one3dConfig
            );

            // Store the promise as the instance
            one3dInstanceRef.current = initPromise;

            initPromise
            .then((successData) => {
                if (!isMountedRef.current || isCleaningUpRef.current) return;
                // Final check - ensure only one canvas exists. Keep the LAST canvas (most recent
                // from the active init); ONE3D uses the most recently created one for rendering.
                const canvases = container.querySelectorAll('canvas');
                if (canvases.length > 1) {
                    console.warn('⚠️ Multiple canvases detected! Keeping the latest, removing older ones.');
                    for (let i = 0; i < canvases.length - 1; i++) {
                        canvases[i].remove();
                    }
                }
                console.log('✅ One3D loaded successfully:', successData);
                if (successData && typeof successData === 'object') {
                    one3dInstanceRef.current = successData;
                }
                isInitializingRef.current = false;
                hasInitializedRef.current = true;
                isCleaningUpRef.current = false;
                setLoadingProgress(100);
                setIsFadingOut(true);

                const loadMs = one3dLoadStartRef.current ? Date.now() - one3dLoadStartRef.current : undefined;
                if (!viewerOpenedEmittedRef.current) {
                    viewerOpenedEmittedRef.current = true;
                    viewerSessionStartRef.current = Date.now();
                    pushViewerEvent('viewer_opened', { entry_source: resolvedEntrySource });
                    const snap = cartRef.current;
                    if (snap && Object.keys(snap).length > 0) {
                        const cartItemCount = Object.values(snap).reduce((n, item) => n + (item?.quantity || 0), 0);
                        pushViewerEvent('viewer_opened_with_cart_items', { cart_item_count: cartItemCount });
                    }
                    if (accessoryId && config?.current_accessory) {
                        pushViewerEvent('viewer_opened_with_preselected_accessory', {
                            accessory_id: accessoryId,
                            part_number: config.current_accessory.part_number || undefined,
                        });
                    }
                }
                if (!firstModelVariantEmittedRef.current) {
                    firstModelVariantEmittedRef.current = true;
                    pushViewerEvent('viewer_model_selected', { model_id: selectedModel.id, model_name: selectedModel.name });
                    pushViewerEvent('viewer_variant_selected', {
                        variant_id: selectedVariant.id,
                        variant_name: selectedVariant.name,
                        model_id: selectedModel.id,
                    });
                    previousModelIdRef.current = selectedModel.id;
                    previousVariantIdRef.current = selectedVariant.id;
                }
                if (!initialModelVariantSourceEmittedRef.current) {
                    initialModelVariantSourceEmittedRef.current = true;
                    pushViewerEvent('viewer_initial_model_variant_set', {
                        model_id: selectedModel.id,
                        variant_id: selectedVariant.id,
                        source: initialModelId || initialVariantId ? 'deeplink' : 'default',
                    });
                }
                pushViewerEvent('viewer_model_variant_loaded', {
                    model_id: selectedModel.id,
                    variant_id: selectedVariant.id,
                    load_time_ms: loadMs,
                });

                setTimeout(() => {
                    if (isMountedRef.current) {
                        const cartSnap = cartRef.current;
                        const hasCart = cartSnap && Object.keys(cartSnap).length > 0;
                        const hasEntry = !!accessoryId;

                        // If we know we'll potentially preload either cart or entry accessories,
                        // turn on the accessories overlay *before* hiding the model overlay so
                        // there is no visual gap between them.
                        if (hasCart || hasEntry) {
                            setPreloadingCartAccessories(true);
                        }

                        setLoading(false);
                        setIsFadingOut(false);
                    }
                }, 500);
            })
            .catch((error) => {
                if (!isMountedRef.current) return;
                console.error('❌ One3D loading error:', error);
                isInitializingRef.current = false;
                hasInitializedRef.current = false;
                pushViewerEvent('viewer_3d_load_error', {
                    error_type: 'load_failed',
                    model_id: selectedModel?.id,
                    variant_id: selectedVariant?.id,
                });
                setError('Failed to load 3D model. Please try again.');
                setLoading(false);
            });
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error('❌ One3D initialization error:', error);
            isInitializingRef.current = false;
            hasInitializedRef.current = false;
            pushViewerEvent('viewer_3d_load_error', {
                error_type: 'init_exception',
                model_id: selectedModel?.id,
                variant_id: selectedVariant?.id,
            });
            setError('Failed to initialize 3D viewer.');
            setLoading(false);
        }
    };

    const handleModelChange = (model) => {
        const prevId = previousModelIdRef.current;
        if (prevId != null && prevId !== model.id) {
            pushViewerEvent('viewer_model_changed', {
                previous_model_id: prevId,
                model_id: model.id,
                model_name: model.name,
            });
        }
        pushViewerEvent('viewer_model_selected', { model_id: model.id, model_name: model.name });
        previousModelIdRef.current = model.id;

        // Reset initialization flags to allow re-initialization
        hasInitializedRef.current = false;
        isInitializingRef.current = false;
        
        setSelectedModel(model);
        // Reset variant to first available for new model
        const variants = config.variants_by_model[model.id] || [];
        if (variants.length > 0) {
            setSelectedVariant(variants[0]);
        } else {
            setSelectedVariant(null);
        }
        // Clear selected accessories and category when model changes
        setSelectedAccessories([]);
        setSelectedCategory(null);
    };

    const handleVariantChange = (variant) => {
        const prevId = previousVariantIdRef.current;
        if (selectedModel && prevId != null && prevId !== variant.id) {
            pushViewerEvent('viewer_variant_changed', {
                previous_variant_id: prevId,
                variant_id: variant.id,
                variant_name: variant.name,
                model_id: selectedModel.id,
            });
        }
        pushViewerEvent('viewer_variant_selected', {
            variant_id: variant.id,
            variant_name: variant.name,
            model_id: selectedModel?.id,
        });
        previousVariantIdRef.current = variant.id;

        // Reset initialization flags to allow re-initialization
        hasInitializedRef.current = false;
        isInitializingRef.current = false;
        
        setSelectedVariant(variant);
        // Clear selected accessories and category when variant changes
        setSelectedAccessories([]);
        setSelectedCategory(null);
    };

    const handleCategoryChange = (category) => {
        pushViewerEvent('viewer_category_selected', {
            category_id: category?.id ?? null,
            category_name: category?.name ?? '',
        });
        setSelectedCategory(category);
        setSelectedAccessories([]); // Clear selected accessories when category changes
    };

    const toggleAccessory = (accessoryCode) => {
        setSelectedAccessories(prev => {
            if (prev.includes(accessoryCode)) {
                return prev.filter(code => code !== accessoryCode);
            } else {
                return [...prev, accessoryCode];
            }
        });
    };

    const resetView = () => {
        if (one3dInstanceRef.current && typeof one3dInstanceRef.current.resetView === 'function') {
            one3dInstanceRef.current.resetView();
        } else if (typeof window.ONE3D !== 'undefined' && typeof window.ONE3D.resetView === 'function') {
            window.ONE3D.resetView();
        }
    };

    const exitAccessoriesView = () => {
        setInAccessoriesView((wasIn) => {
            if (wasIn) pushViewerEvent('viewer_exterior_view_selected');
            return false;
        });
        setCurrentView('exterior');
        if (typeof window.ONE3D === 'undefined') return;
        if (typeof window.ONE3D.exitAccessoriesView === 'function') {
            window.ONE3D.exitAccessoriesView();
        } else if (typeof window.ONE3D.exteriorView === 'function') {
            // Fallback for environments where exitAccessoriesView is not yet available
            window.ONE3D.exteriorView();
        }
    };

    const toggleFeatureHp = () => {
        setFeatureHpEnabled(prev => !prev);
        if (typeof window.ONE3D !== 'undefined' && typeof window.ONE3D.toggleFeatureHp === 'function') {
            window.ONE3D.toggleFeatureHp();
        }
    };

    const setView = (viewType) => {
        setCurrentView(viewType);
        if (typeof window.ONE3D === 'undefined') return;

        switch(viewType) {
            case 'exterior':
                if (typeof window.ONE3D.exteriorView === 'function') {
                    window.ONE3D.exteriorView();
                }
                break;
            case 'interior':
                if (typeof window.ONE3D.interiorView === 'function') {
                    window.ONE3D.interiorView();
                }
                break;
            default:
                break;
        }
    };

    // Sync selectedAccessories with One3D when they change (after initial load)
    useEffect(() => {
        // Skip if still loading or One3D not ready
        if (loading || !hasInitializedRef.current || typeof window.ONE3D === 'undefined') {
            return;
        }

        // Don't sync during initial load (preConfig handles that)
        if (!one3dInstanceRef.current) {
            return;
        }

        // This will be handled by individual add/remove calls
        // No need to sync entire array - just let the buttons handle it
    }, [selectedAccessories, loading]);

    // Preload 3D accessories: entry accessory (from detail page) + cart items. All go through "Loading your accessories" overlay.
    useEffect(() => {
        if (
            loading ||
            !hasInitializedRef.current ||
            hasPreloadedFromCartRef.current ||
            typeof window.ONE3D === 'undefined' ||
            typeof window.ONE3D.addAccessory !== 'function' ||
            !availableAccessories.length
        ) {
            return;
        }

        const cartIds = cart ? Object.keys(cart).map(id => String(id)) : [];
        const toPreloadFromCart = cart && cartIds.length > 0
            ? availableAccessories.filter(
                (a) => a.one3d_item_code && cartIds.includes(String(a.id)) && !selectedAccessories.includes(a.one3d_item_code)
            )
            : [];

        const entryAccessory = accessoryId
            ? availableAccessories.find((a) => a.id === accessoryId && a.one3d_item_code)
            : null;
        const entryCode = entryAccessory?.one3d_item_code;
        const needsEntry = entryCode && !selectedAccessories.includes(entryCode);

        const cartCodes = toPreloadFromCart.map((a) => a.one3d_item_code);
        const preloadCodes = needsEntry
            ? [entryCode, ...cartCodes.filter((c) => c !== entryCode)]
            : cartCodes;

        if (preloadCodes.length === 0) {
            setPreloadingCartAccessories(false);
            return;
        }

        hasPreloadedFromCartRef.current = true;
        setPreloadingCartAccessories(true);
        setPreloadProgress(0);
        const total = preloadCodes.length;
        let preloadFails = 0;

        const addNext = (index) => {
            if (!isMountedRef.current || index >= preloadCodes.length) {
                setPreloadProgress(100);
                setSelectedAccessories((prev) => [...new Set([...prev, ...preloadCodes])]);
                setPreloadingCartAccessories(false);
                if (preloadCodes.length > 0 && preloadFails >= preloadCodes.length) {
                    pushViewerEvent('viewer_preload_cart_failed', { cart_item_count: preloadCodes.length });
                }
                return;
            }
            const code = preloadCodes[index];
            window.ONE3D.addAccessory(code)
                .then(() => {
                    setPreloadProgress(total ? Math.round(((index + 1) / total) * 100) : 0);
                    addNext(index + 1);
                })
                .catch((err) => {
                    console.warn('Preload addAccessory failed for', code, err);
                    preloadFails += 1;
                    addNext(index + 1);
                });
        };

        addNext(0);
    }, [loading, availableAccessories, cart, selectedAccessories, accessoryId]);

    // Suppress known One3D internal error ("reading 'y'") while viewer is mounted - happens in their mousemove/RAF after model loads
    useLayoutEffect(() => {
        const isOne3dYError = (msgOrErr) => {
            const str = typeof msgOrErr === 'string' ? msgOrErr : (msgOrErr?.message ?? '');
            if (!str) return false;
            return str.includes("reading 'y'") || str.includes('getClientRects') || (str.includes("Cannot read properties of undefined") && str.includes("'y'"));
        };
        const isOne3dScript = (source) => (typeof source === 'string' && source.includes('one3d_functions'));

        const prevOnError = window.onerror;
        const handleError = (msg, source, lineno, colno, err) => {
            if (isOne3dYError(msg) || isOne3dYError(err)) return true;
            if ((msg === 'Script error.' || !msg) && (isOne3dScript(source) || typeof window.ONE3D !== 'undefined')) return true;
            return prevOnError ? prevOnError(msg, source, lineno, colno, err) : false;
        };
        const captureError = (e) => {
            const msg = e?.message ?? e?.error?.message ?? '';
            if (isOne3dYError(msg) || isOne3dYError(e?.error)) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
            }
        };

        const origRAF = window.requestAnimationFrame;
        window.requestAnimationFrame = function (callback) {
            return origRAF(function (time) {
                try {
                    callback(time);
                } catch (err) {
                    if (isOne3dYError(err?.message)) return;
                    throw err;
                }
            });
        };

        const origAddEventListener = EventTarget.prototype.addEventListener;
        const one3dEventTypes = ['mousemove', 'pointermove'];
        EventTarget.prototype.addEventListener = function (type, listener, options) {
            if (one3dEventTypes.indexOf(type) !== -1 && typeof listener === 'function') {
                const wrapped = function (e) {
                    try {
                        return listener.call(this, e);
                    } catch (err) {
                        if (isOne3dYError(err?.message)) return;
                        throw err;
                    }
                };
                return origAddEventListener.call(this, type, wrapped, options);
            }
            return origAddEventListener.call(this, type, listener, options);
        };

        window.onerror = handleError;
        window.addEventListener('error', captureError, { capture: true });

        return () => {
            window.requestAnimationFrame = origRAF;
            EventTarget.prototype.addEventListener = origAddEventListener;
            window.onerror = prevOnError;
            window.removeEventListener('error', captureError, { capture: true });
        };
    }, []);

    // Listen for fullscreen changes (including ESC key)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
            console.log('🖥️ Fullscreen changed:', isCurrentlyFullscreen);
            setIsFullscreen(isCurrentlyFullscreen);
        };

        // Check initial state
        const initialFullscreen = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );
        setIsFullscreen(initialFullscreen);

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
        }, []);

    useEffect(() => {
        if (isFullscreen && !prevFullscreenRef.current) pushViewerEvent('viewer_fullscreen_entered');
        if (!isFullscreen && prevFullscreenRef.current) pushViewerEvent('viewer_fullscreen_exited');
        prevFullscreenRef.current = isFullscreen;
    }, [isFullscreen]);

    useEffect(() => {
        const el = one3dContainerRef.current;
        if (!el || cameraInteractionEmittedRef.current) return;
        const onFirstPointer = (e) => {
            if (cameraInteractionEmittedRef.current) return;
            cameraInteractionEmittedRef.current = true;
            const type = e.type === 'wheel' ? 'zoom' : 'rotate';
            pushViewerEvent('viewer_camera_interaction', { interaction_type: type });
        };
        el.addEventListener('pointerdown', onFirstPointer, { passive: true });
        el.addEventListener('wheel', onFirstPointer, { passive: true });
        return () => {
            el.removeEventListener('pointerdown', onFirstPointer);
            el.removeEventListener('wheel', onFirstPointer);
        };
    }, [loading]);

    if (error) {
        return (
            <div className="three-d-error">
                <p>{error}</p>
                <button onClick={() => teardownAndClose(true)}>
                    <span language="ar">إغلاق</span>
                    <span language="en">Close</span>
                </button>
            </div>
        );
    }

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            // Request fullscreen on the viewer section (which contains the 3D canvas)
            const elementToFullscreen = viewerSectionRef.current || one3dContainerRef.current;
            if (elementToFullscreen) {
                if (elementToFullscreen.requestFullscreen) {
                    elementToFullscreen.requestFullscreen();
                } else if (elementToFullscreen.webkitRequestFullscreen) {
                    elementToFullscreen.webkitRequestFullscreen();
                } else if (elementToFullscreen.mozRequestFullScreen) {
                    elementToFullscreen.mozRequestFullScreen();
                } else if (elementToFullscreen.msRequestFullscreen) {
                    elementToFullscreen.msRequestFullscreen();
                }
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    // Per ONE3D docs: setAccessorieCamera sets camera BEFORE adding accessory for smoother UX.
    // Call setAccessorieCamera first, then addAccessory in .then() - doc example: setAccessorieCamera(cam).then(() => addAccessory(acc))
    const addAccessoryWithCamera = (accessoryCode, cameraId, onSuccess, onError) => {
        const doAdd = () => {
            return window.ONE3D.addAccessory(accessoryCode)
                .then((result) => {
                    if (typeof onSuccess === 'function') {
                        onSuccess(result);
                    }
                    setInAccessoriesView(true);
                })
                .catch(onError);
        };
        const cameraIdTrimmed = cameraId ? String(cameraId).trim() : '';
        if (cameraIdTrimmed && typeof window.ONE3D.setAccessorieCamera === 'function') {
            window.ONE3D.setAccessorieCamera(cameraIdTrimmed).then(() => doAdd()).catch((e) => {
                console.warn('setAccessorieCamera failed, adding accessory anyway:', e);
                doAdd();
            });
        } else {
            doAdd();
        }
    };

    const handleAddAccessory = async (accessory, options = {}) => {
        const accessoryCode = accessory.one3d_item_code;
        const shouldAddToCart = options.addToCart !== false;
        console.log('🔧 handleAddAccessory called:', accessoryCode, accessory, 'addToCart:', shouldAddToCart);
        console.log('🔧 Current selectedAccessories:', selectedAccessories);
        
        if (!accessoryCode) {
            console.error('❌ No accessory code provided');
            return;
        }
        
        if (selectedAccessories.includes(accessoryCode)) {
            console.log('⚠️ Accessory already added:', accessoryCode);
            return;
        }

        // Check if One3D is ready
        if (typeof window.ONE3D === 'undefined') {
            console.error('❌ ONE3D library not loaded');
            return;
        }

        if (typeof window.ONE3D.addAccessory !== 'function') {
            console.error('❌ ONE3D.addAccessory is not a function');
            return;
        }

        // Check compatibility before adding
        if (!selectedModel || !selectedVariant) {
            console.error('❌ Model or variant not selected');
            return;
        }

        pushViewerEvent('viewer_accessory_add_started', {
            accessory_id: accessory.id,
            part_number: accessory.part_number,
            category_id: selectedCategory?.id ?? null,
            category_name: selectedCategory?.name ?? '',
        });

        const notifyAccessoryAddedToAnalytics = (addedToCart) => {
            accessoriesAddedCountSessionRef.current += 1;
            pushViewerEvent('viewer_accessory_added', {
                ...accessoryAnalyticsPayload(accessory),
                category_id: selectedCategory?.id ?? null,
                add_to_cart: !!addedToCart,
            });
            if (addedToCart) {
                pushViewerEvent('viewer_accessory_added_to_cart_via_card_click', {
                    accessory_id: accessory.id,
                    part_number: accessory.part_number,
                });
            }
            pushViewerEvent('viewer_total_accessories_on_car', { count: selectedAccessories.length + 1 });
        };

        // Set updating state
        setIsUpdatingAccessories(true);

        try {
            const country_id = localStorage.getItem('country_id');
            const market_id = localStorage.getItem('market_id') || '1';
            
            // Get selected accessory IDs (not codes) for compatibility check
            const selectedAccessoryIds = availableAccessories
                .filter(a => a.one3d_item_code && selectedAccessories.includes(a.one3d_item_code))
                .map(a => a.id);
            
            console.log('🔍 Selected accessory codes:', selectedAccessories);
            console.log('🔍 Selected accessory IDs:', selectedAccessoryIds);
            console.log('🔍 Available accessories count:', availableAccessories.length);

            const token = 'Bearer ' + process.env.REACT_APP_FORD_API_TOKEN;
            
            // Prepare request payload with proper types
            const payload = {
                model_id: parseInt(selectedModel.id, 10),
                variant_id: parseInt(selectedVariant.id, 10),
                market_id: parseInt(market_id, 10),
                selected_accessories: selectedAccessoryIds.map(id => parseInt(id, 10))
            };
            
            // Only include country_id if it exists and is not empty
            if (country_id && country_id !== 'null' && country_id !== '') {
                payload.country_id = parseInt(country_id, 10);
            }
            
            console.log('🔍 Compatibility check payload:', payload);
            
            const response = await axios.post(
                `${process.env.REACT_APP_FORD_API_URL}/api/v1/accessories/${accessory.id}/compatibility-check`,
                payload,
                {
                    headers: {
                        'Authorization': token,
                        'X-Locale': localStorage.getItem('language') || 'en'
                    }
                }
            );

            const compatibility = response.data;
            console.log('🔍 Compatibility check result:', compatibility);

            // Check for conflicts - show modal
            if (!compatibility.can_add || compatibility.conflicts.length > 0) {
                setCompatibilityModal({
                    type: 'conflict',
                    accessory: accessory,
                    conflicts: compatibility.conflicts,
                    onConfirm: () => {
                        // Set updating state
                        setIsUpdatingAccessories(true);
                        
                        // On Yes: Remove conflicting accessories and add the new one
                        // Handle both forward and reverse conflicts
                        const conflictsToRemove = compatibility.conflicts.map(c => {
                            // Forward conflict: c.accessory_id is the conflicting accessory
                            // Reverse conflict: c.accessory is the conflicting accessory
                            const conflictAccessory = c.accessory || availableAccessories.find(a => a.id === c.accessory_id);
                            if (!conflictAccessory) return null;
                            
                            // Find the one3d_item_code for this accessory
                            return conflictAccessory.one3d_item_code || availableAccessories.find(a => a.id === conflictAccessory.id)?.one3d_item_code;
                        }).filter(Boolean);

                        // Remove conflicting accessories from selected
                        setSelectedAccessories(prev => {
                            const newSelected = prev.filter(code => !conflictsToRemove.includes(code));
                            // Add the new accessory
                            if (!newSelected.includes(accessoryCode)) {
                                newSelected.push(accessoryCode);
                            }
                            return newSelected;
                        });

                        // Remove conflicting accessories from One3D first, then cart
                        const conflictRemovalPromises = conflictsToRemove.map(code => {
                            const conflictAccessory = availableAccessories.find(a => a.one3d_item_code === code);
                            if (window.ONE3D && window.ONE3D.removeAccessory) {
                                return window.ONE3D.removeAccessory(code)
                                    .then(() => {
                                        // Only remove from cart after One3D succeeds
                                        if (conflictAccessory && removeFromCart) {
                                            removeFromCart(conflictAccessory.id);
                                        }
                                        return { success: true, code, accessory: conflictAccessory };
                                    })
                                    .catch(err => {
                                        console.error('Failed to remove conflicting accessory:', err);
                                        return { success: false, code, accessory: conflictAccessory, error: err };
                                    });
                            }
                            return Promise.resolve({ success: false, code, accessory: conflictAccessory });
                        });

                        // Wait for all conflicts to be removed, then add the new accessory
                        Promise.all(conflictRemovalPromises)
                            .then(() => {
                                setSelectedAccessories(prev => [...prev, accessoryCode]);
                                addAccessoryWithCamera(
                                    accessoryCode,
                                    accessory.one3d_camera_id,
                                    (result) => {
                                        console.log('✅ Accessory added successfully after removing conflicts:', result);
                                        if (shouldAddToCart && addToCart && typeof addToCart === 'function') {
                                            addToCart({ id: accessory.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                                        }
                                        notifyAccessoryAddedToAnalytics(shouldAddToCart);
                                        setIsUpdatingAccessories(false);
                                    },
                                    (error) => {
                                        console.error('❌ Failed to add accessory:', error);
                                        pushViewerEvent('viewer_accessory_add_failed', {
                                            accessory_id: accessory.id,
                                            error_type: 'load_error',
                                        });
                                        pushViewerEvent('viewer_accessory_load_error', {
                                            accessory_id: accessory.id,
                                            error_type: 'one3d_add_failed',
                                        });
                                        setSelectedAccessories(prev => {
                                            const newSelected = prev.filter(code => code !== accessoryCode);
                                            conflictsToRemove.forEach(code => {
                                                if (!newSelected.includes(code)) newSelected.push(code);
                                            });
                                            return newSelected;
                                        });
                                        if (shouldAddToCart) {
                                            conflictsToRemove.forEach(code => {
                                                const conflictAccessory = availableAccessories.find(a => a.one3d_item_code === code);
                                                if (conflictAccessory && addToCart) {
                                                    addToCart({ id: conflictAccessory.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                                                }
                                            });
                                        }
                                        setIsUpdatingAccessories(false);
                                    }
                                );
                            })
                            .catch((error) => {
                                console.error('❌ Failed removing conflicts:', error);
                                setIsUpdatingAccessories(false);
                            });

                        setCompatibilityModal(null);
                    },
                    onCancel: () => {
                        // On No: Just close the modal, don't add anything
                        setIsUpdatingAccessories(false); // Reset updating state
                        setCompatibilityModal(null);
                    }
                });
                return;
            }

            // Check for dependencies or replacements - show modal
            if (compatibility.dependencies.length > 0 || compatibility.replacements.length > 0) {
                setCompatibilityModal({
                    type: compatibility.replacements.length > 0 ? 'replacement' : 'dependency',
                    accessory: accessory,
                    dependencies: compatibility.dependencies,
                    replacements: compatibility.replacements,
                    onConfirm: () => {
                        // Set updating state
                        setIsUpdatingAccessories(true);
                        
                        // Auto-add dependencies
                        const dependenciesToAdd = compatibility.dependencies.map(d => {
                            const depAccessory = availableAccessories.find(a => a.id === d.accessory_id);
                            return depAccessory?.one3d_item_code;
                        }).filter(Boolean);

                        // Auto-remove replacements
                        const replacementsToRemove = compatibility.replacements.map(r => {
                            const repAccessory = availableAccessories.find(a => a.id === r.accessory_id);
                            return repAccessory?.one3d_item_code;
                        }).filter(Boolean);

                        if (replacementsToRemove.length > 0) {
                            pushViewerEvent('viewer_replacements_removed', {
                                new_accessory_id: accessory.id,
                                replaced_count: replacementsToRemove.length,
                            });
                        }

                        // Remove replacements from selected
                        setSelectedAccessories(prev => {
                            const newSelected = prev.filter(code => !replacementsToRemove.includes(code));
                            // Add dependencies
                            dependenciesToAdd.forEach(code => {
                                if (!newSelected.includes(code)) {
                                    newSelected.push(code);
                                }
                            });
                            return newSelected;
                        });

                        // Remove replacements from One3D first, then cart
                        const replacementPromises = replacementsToRemove.map(code => {
                            const replacementAccessory = availableAccessories.find(a => a.one3d_item_code === code);
                            if (window.ONE3D && window.ONE3D.removeAccessory) {
                                return window.ONE3D.removeAccessory(code)
                                    .then(() => {
                                        // Only remove from cart after One3D succeeds
                                        if (replacementAccessory && removeFromCart) {
                                            removeFromCart(replacementAccessory.id);
                                        }
                                        return { success: true, code, accessory: replacementAccessory };
                                    })
                                    .catch(err => {
                                        console.error('Failed to remove replacement accessory:', err);
                                        return { success: false, code, accessory: replacementAccessory, error: err };
                                    });
                            }
                            return Promise.resolve({ success: false, code, accessory: replacementAccessory });
                        });

                        // Add dependencies to One3D first, then cart
                        const dependencyPromises = dependenciesToAdd.map(code => {
                            const dependencyAccessory = availableAccessories.find(a => a.one3d_item_code === code);
                            if (window.ONE3D && window.ONE3D.addAccessory) {
                                return window.ONE3D.addAccessory(code)
                                    .then(() => {
                                        // Only add to cart after One3D succeeds
                                        if (shouldAddToCart && dependencyAccessory && addToCart) {
                                            addToCart({
                                                id: dependencyAccessory.id,
                                                quantity: 1,
                                                category_id: localStorage.getItem('category_id') || ''
                                            }, 1);
                                        }
                                        return { success: true, code, accessory: dependencyAccessory };
                                    })
                                    .catch(err => {
                                        console.error('Failed to add dependency accessory:', err);
                                        return { success: false, code, accessory: dependencyAccessory, error: err };
                                    });
                            }
                            return Promise.resolve({ success: false, code, accessory: dependencyAccessory });
                        });

                        // Wait for all replacements and dependencies to complete, then add main accessory
                        Promise.all([...replacementPromises, ...dependencyPromises])
                            .then(() => {
                                setSelectedAccessories(prev => [...prev, accessoryCode]);
                                addAccessoryWithCamera(
                                    accessoryCode,
                                    accessory.one3d_camera_id,
                                    (result) => {
                                        console.log('✅ Accessory added successfully:', result);
                                        if (shouldAddToCart && addToCart && typeof addToCart === 'function') {
                                            addToCart({ id: accessory.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                                        }
                                        notifyAccessoryAddedToAnalytics(shouldAddToCart);
                                        setIsUpdatingAccessories(false);
                                    },
                                    (error) => {
                                        console.error('❌ Failed to add accessory:', error);
                                        pushViewerEvent('viewer_accessory_add_failed', {
                                            accessory_id: accessory.id,
                                            error_type: 'load_error',
                                        });
                                        pushViewerEvent('viewer_accessory_load_error', {
                                            accessory_id: accessory.id,
                                            error_type: 'one3d_add_failed',
                                        });
                                        setSelectedAccessories(prev => {
                                            const newSelected = prev.filter(code => code !== accessoryCode);
                                            replacementsToRemove.forEach(code => {
                                                if (!newSelected.includes(code)) newSelected.push(code);
                                            });
                                            return newSelected.filter(code => !dependenciesToAdd.includes(code));
                                        });
                                        if (shouldAddToCart) {
                                            replacementsToRemove.forEach(code => {
                                                const replacementAccessory = availableAccessories.find(a => a.one3d_item_code === code);
                                                if (replacementAccessory && addToCart) {
                                                    addToCart({ id: replacementAccessory.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                                                }
                                            });
                                        }
                                        dependenciesToAdd.forEach(code => {
                                            const dependencyAccessory = availableAccessories.find(a => a.one3d_item_code === code);
                                            if (dependencyAccessory && removeFromCart) {
                                                removeFromCart(dependencyAccessory.id);
                                            }
                                        });
                                        setIsUpdatingAccessories(false);
                                    }
                                );
                            })
                            .catch((error) => {
                                console.error('❌ Failed replacements/dependencies:', error);
                                setIsUpdatingAccessories(false);
                            });

                        setCompatibilityModal(null);
                    },
                    onCancel: () => {
                        setIsUpdatingAccessories(false); // Reset updating state
                        setCompatibilityModal(null);
                    }
                });
                return;
            }

            // No conflicts, dependencies, or replacements - add directly
            setSelectedAccessories([...selectedAccessories, accessoryCode]);
            addAccessoryWithCamera(
                accessoryCode,
                accessory.one3d_camera_id,
                (result) => {
                    console.log('✅ Accessory added successfully:', result);
                    if (shouldAddToCart && addToCart && typeof addToCart === 'function') {
                        addToCart({ id: accessory.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                    }
                    notifyAccessoryAddedToAnalytics(shouldAddToCart);
                    setIsUpdatingAccessories(false);
                },
                (error) => {
                    console.error('❌ Failed to add accessory:', error);
                    pushViewerEvent('viewer_accessory_add_failed', {
                        accessory_id: accessory.id,
                        error_type: 'load_error',
                    });
                    pushViewerEvent('viewer_accessory_load_error', {
                        accessory_id: accessory.id,
                        error_type: 'one3d_add_failed',
                    });
                    setSelectedAccessories(prev => prev.filter(code => code !== accessoryCode));
                    setIsUpdatingAccessories(false);
                }
            );
        } catch (error) {
            console.error('❌ Error checking compatibility:', error);
            console.error('❌ Error response:', error.response?.data);
            console.error('❌ Error status:', error.response?.status);
            pushViewerEvent('viewer_accessory_add_failed', {
                accessory_id: accessory.id,
                error_type: error.response ? 'compatibility_error' : 'network_error',
            });
            // If compatibility check fails, proceed with adding (fail open)
            setSelectedAccessories([...selectedAccessories, accessoryCode]);
            addAccessoryWithCamera(
                accessoryCode,
                accessory.one3d_camera_id,
                (result) => {
                    console.log('✅ Accessory added successfully:', result);
                    if (shouldAddToCart && addToCart && typeof addToCart === 'function') {
                        addToCart({ id: accessory.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                    }
                    notifyAccessoryAddedToAnalytics(shouldAddToCart);
                    setIsUpdatingAccessories(false);
                },
                (err) => {
                    console.error('❌ Failed to add accessory:', err);
                    pushViewerEvent('viewer_accessory_add_failed', {
                        accessory_id: accessory.id,
                        error_type: 'load_error',
                    });
                    pushViewerEvent('viewer_accessory_load_error', {
                        accessory_id: accessory.id,
                        error_type: 'one3d_add_failed',
                    });
                    setSelectedAccessories(prev => prev.filter(code => code !== accessoryCode));
                    setIsUpdatingAccessories(false);
                }
            );
        }
    };

    const handleRemoveAccessory = async (accessoryCode, options = {}) => {
        const shouldRemoveFromCart = options.removeFromCart !== false;
        console.log('🔧 handleRemoveAccessory called:', accessoryCode, 'removeFromCart:', shouldRemoveFromCart);

        if (!accessoryCode) {
            console.error('❌ No accessory code provided');
            return;
        }

        if (!selectedAccessories.includes(accessoryCode)) {
            console.log('⚠️ Accessory not in selected list:', accessoryCode);
            return;
        }

        // Check if One3D is ready
        if (typeof window.ONE3D === 'undefined') {
            console.error('❌ ONE3D library not loaded');
            return;
        }

        if (typeof window.ONE3D.removeAccessory !== 'function') {
            console.error('❌ ONE3D.removeAccessory is not a function');
            return;
        }

        const accessoryToRemove = availableAccessories.find(a => a.one3d_item_code === accessoryCode);
        if (!accessoryToRemove) {
            console.error('❌ Accessory not found in available list');
            return;
        }

        setIsUpdatingAccessories(true);

        // Fetch dependents (e.g. cross bars when removing roof rails) so we auto-remove them too
        let dependentIds = [];
        if (selectedModel && selectedVariant && accessoryToRemove.id) {
            try {
                const country_id = localStorage.getItem('country_id');
                const market_id = localStorage.getItem('market_id') || '1';
                const payload = {
                    model_id: parseInt(selectedModel.id, 10),
                    variant_id: parseInt(selectedVariant.id, 10),
                    market_id: parseInt(market_id, 10),
                };
                if (country_id && country_id !== 'null' && country_id !== '') {
                    payload.country_id = parseInt(country_id, 10);
                }
                const res = await axios.post(
                    `${process.env.REACT_APP_FORD_API_URL}/api/v1/accessories/${accessoryToRemove.id}/removal-dependents`,
                    payload,
                    {
                        headers: {
                            'Authorization': 'Bearer ' + process.env.REACT_APP_FORD_API_TOKEN,
                            'Content-Type': 'application/json',
                            'X-Locale': localStorage.getItem('language') || 'en',
                        },
                    }
                );
                if (res.data && Array.isArray(res.data.dependents)) {
                    dependentIds = res.data.dependents;
                }
            } catch (err) {
                console.warn('Could not fetch removal dependents:', err?.response?.status || err.message);
            }
        }

        // Dependents that are currently selected (by one3d code)
        const dependentCodes = availableAccessories
            .filter(a => dependentIds.includes(a.id) && selectedAccessories.includes(a.one3d_item_code))
            .map(a => a.one3d_item_code);
        const allCodesToRemove = [...dependentCodes, accessoryCode];

        if (dependentCodes.length > 0) {
            pushViewerEvent('viewer_dependent_accessories_removed', {
                parent_accessory_id: accessoryToRemove.id,
                dependent_count: dependentCodes.length,
            });
        }

        // Update selection in one go
        setSelectedAccessories(prev => prev.filter(code => !allCodesToRemove.includes(code)));

        const removeOneFromOne3DAndCart = (code) => {
            const acc = availableAccessories.find(a => a.one3d_item_code === code);
            const removePromise = window.ONE3D.removeAccessory(code);
            if (shouldRemoveFromCart && acc && removeFromCart && typeof removeFromCart === 'function') {
                removeFromCart(acc.id);
            }
            return removePromise;
        };

        try {
            // Remove dependents first (e.g. cross bars), then main accessory (e.g. roof rails)
            for (const code of allCodesToRemove) {
                await removeOneFromOne3DAndCart(code);
            }
            accessoriesRemovedCountSessionRef.current += 1;
            pushViewerEvent('viewer_accessory_removed', {
                accessory_id: accessoryToRemove.id,
                part_number: accessoryToRemove.part_number,
                removed_from_cart: shouldRemoveFromCart,
            });
            if (shouldRemoveFromCart) {
                pushViewerEvent('viewer_accessory_removed_from_cart_via_card_click', {
                    accessory_id: accessoryToRemove.id,
                    part_number: accessoryToRemove.part_number,
                });
            }
            pushViewerEvent('viewer_total_accessories_on_car', {
                count: selectedAccessories.length - allCodesToRemove.length,
            });
            if (!shouldRemoveFromCart) {
                exitAccessoriesView();
            }
        } catch (error) {
            console.error('❌ Failed to remove accessory:', error);
            // Revert selection on error
            setSelectedAccessories(prev => [...new Set([...prev, ...allCodesToRemove])]);
            if (shouldRemoveFromCart) {
                for (const code of allCodesToRemove) {
                    const acc = availableAccessories.find(a => a.one3d_item_code === code);
                    if (acc && addToCart && typeof addToCart === 'function') {
                        addToCart({ id: acc.id, quantity: 1, category_id: localStorage.getItem('category_id') || '' }, 1);
                    }
                }
            }
        } finally {
            setIsUpdatingAccessories(false);
        }
    };

    const handleShowAccessoryDetails = async (accessory) => {
        if (!accessory || !accessory.id) {
            console.error('❌ Invalid accessory for details');
            return;
        }

        detailAccessoryIdRef.current = accessory.id;
        pushViewerEvent('viewer_accessory_details_opened', {
            accessory_id: accessory.id,
            part_number: accessory.part_number,
        });

        // Set loading state
        setAccessoryDetailFlyout({ accessory: null, loading: true });

        try {
            const data = await apiRequest(`/api/v1/accessories/${accessory.id}`, {
                category_id: localStorage.getItem('category_id') || ''
            });
            setAccessoryDetailFlyout({ accessory: data, loading: false });
        } catch (error) {
            console.error('❌ Error fetching accessory details:', error);
            pushViewerEvent('viewer_accessory_details_load_failed', { accessory_id: accessory.id });
            setAccessoryDetailFlyout({ accessory: null, loading: false });
        }
    };

    const closeAccessoryDetailFlyout = () => {
        if (detailAccessoryIdRef.current) {
            pushViewerEvent('viewer_accessory_details_closed', { accessory_id: detailAccessoryIdRef.current });
        }
        detailAccessoryIdRef.current = null;
        setAccessoryDetailFlyout(null);
    };

    return (
        <div className="personalize-3d-container">
            {/* Header */}
            <div className="personalize-header">
                <h1 className="personalize-title">
                    <span language="ar">مواءمة {selectedModel?.name || ''} بشكل شخصي</span>
                    <span language="en">Personalize {selectedModel?.name || ''}</span>
                </h1>
                <button className="personalize-close" onClick={() => teardownAndClose(true)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                    </svg>
                </button>
            </div>

            {/* Main Content */}
            <div className="personalize-content">
                {/* Left Side - 3D Viewer */}
                <div className="personalize-viewer-section" ref={viewerSectionRef}>
                    {loading && (
                        <div className={`three-d-loading three-d-loader-model ${isFadingOut ? 'fade-out' : ''}`} style={{ zIndex: 1001 }}>
                            <div className="spinner"></div>
                            <p>
                                <span language="ar">جاري تحميل النموذج ثلاثي الأبعاد...</span>
                                <span language="en">Loading 3D Model...</span>
                            </p>
                            <div className="progress-bar-container">
                                <div className="progress-bar" style={{ width: `${loadingProgress}%` }}></div>
                            </div>
                            <p className="progress-text">{loadingProgress}%</p>
                        </div>
                    )}

                    {/* Overlay while cart accessories are added to the 3D model (stacked behind model loader when both show) */}
                    {(preloadingCartAccessories || (loading && cart && Object.keys(cart).length > 0)) && (
                        <div className="three-d-loading three-d-preload-accessories" style={{ zIndex: 1000 }}>
                            <div className="spinner"></div>
                            <p>
                                <span language="ar">تحميل ملحقاتك...</span>
                                <span language="en">Loading your accessories...</span>
                            </p>
                            <div className="progress-bar-container">
                                <div className="progress-bar" style={{ width: `${preloadProgress}%` }}></div>
                            </div>
                            <p className="progress-text">{preloadProgress}%</p>
                        </div>
                    )}

                    {/* Viewer Controls Above */}
                    <div className="viewer-top-controls">
                        <button
                            className="view-360-btn"
                            style={{ minWidth: 116 }}
                            onClick={() => {
                                pushViewerEvent('viewer_360_view_clicked');
                                exitAccessoriesView();
                            }}
                            disabled={!inAccessoriesView}
                        >
                            <img src="/images/3d-icon-white-128x.png" alt="3D" style={{ width: '24px', height: '24px' }} />
                            <span language="ar">منظر 360 درجة</span>
                            <span language="en">360 View</span>
                        </button>
                        <button className="fullscreen-btn" onClick={toggleFullscreen} title="Fullscreen">
                            <img src="/images/3d-icon-fullscreen-256x.png" alt="Fullscreen" style={{ width: '24px', height: '24px' }} />
                        </button>
                    </div>

                    {/* One3D Container - Simple like POC */}
                    <div 
                        id="one3d" 
                        ref={one3dContainerRef}
                    ></div>

                    {/* Disclaimer - bottom right of 3D canvas (click to open inline disclaimer) */}
                    <button
                        type="button"
                        className="three-d-canvas-disclaimer"
                        onClick={() => {
                            pushViewerEvent('viewer_disclaimer_opened');
                            setShowDisclaimerModal(true);
                        }}
                        aria-label="Important note - view disclaimer"
                    >
                        <span language="en">Imp. Note*</span>
                        <span language="ar">ملاحظة مهمة</span>
                    </button>

                    {/* Inline disclaimer box - bottom right of 3D viewer */}
                    {showDisclaimerModal && (
                        <div className="disclaimer-inline-box">
                            <button
                                type="button"
                                className="disclaimer-inline-close"
                                onClick={() => {
                                    pushViewerEvent('viewer_disclaimer_closed');
                                    setShowDisclaimerModal(false);
                                }}
                                aria-label="Close"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                                </svg>
                            </button>
                            <p className="disclaimer-inline-text">
                                <span language="en">The configurator/ visualizer shown is showcased for reference to visualize the car in 3D. The actual color, price and design references are subject to change.</span>
                                <span language="ar">يتم عرض أداة التكوين/التصور المعروضة كمرجع لتصور السيارة بتقنية 3D ثلاثية الأبعاد. تخضع الألوان والسعر والتصميمات الفعلية للتغيير.</span>
                            </p>
                        </div>
                    )}
                </div>
                {/* End of personalize-viewer-section */}

                {/* Right Side - Accessories Panel */}
                {!isFullscreen && (
                    <div className="personalize-accessories-panel">
                    <div className="accessories-header">
                        {selectedCategory && (
                            <button 
                                className="back-categories-btn"
                                onClick={() => {
                                    pushViewerEvent('viewer_back_to_categories_clicked');
                                    setSelectedCategory(null);
                                    setSelectedAccessories([]);
                                }}
                            >
                                <span language="ar">← جميع الفئات</span>
                                <span language="en">← All Categories</span>
                            </button>
                        )}
                        {/* Category header when a category is selected */}
                        {selectedCategory && (
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                <h2 className="accessories-category mb-0">
                                    {selectedCategory.name}
                                </h2>
                                {process.env.REACT_APP_BRAND_ID === '1' && (availableAccessories.some(a => a.one3d_item_code) || show3dOnlyInViewer) && (
                                    <label className="d-flex align-items-center gap-2 mb-0 accessory-show-3d-only" style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={show3dOnlyInViewer}
                                            onChange={(e) => {
                                                const enabled = e.target.checked;
                                                pushViewerEvent('viewer_show_3d_only_toggled', { enabled });
                                                setShow3dOnlyInViewer(enabled);
                                            }}
                                            className="form-check-input"
                                        />
                                        <span>{(localStorage.getItem('language') || 'en') === 'ar' ? 'منظر ثلاثي الأبعاد 3D فقط' : 'Show 3D Only'}</span>
                                    </label>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="accessories-list">
                        {!selectedCategory ? (
                            <CategoryList
                                categories={categories}
                                cart={cart}
                                cartCountsByCategory={cartCountsByCategory}
                                onCategoryChange={handleCategoryChange}
                            />
                        ) : (
                            <>
                                {process.env.REACT_APP_BRAND_ID === '1' && show3dOnlyInViewer && availableAccessories.filter(a => a.one3d_item_code).length === 0 ? (
                                    <div className="py-4 text-center text-muted" style={{ fontSize: '0.9375rem' }}>
                                        {(localStorage.getItem('language') || 'en') === 'ar' ? 'لم يتم العثور على ملحقات.' : 'No accessories found.'}
                                    </div>
                                ) : (
                                    <AccessoryList
                                        loading={loading}
                                        accessoriesLoading={accessoriesLoading}
                                        availableAccessories={show3dOnlyInViewer ? availableAccessories.filter(a => a.one3d_item_code) : availableAccessories}
                                        selectedAccessories={selectedAccessories}
                                        cart={cart}
                                        isUpdatingAccessories={isUpdatingAccessories}
                                        addToCart={addToCart}
                                        removeFromCart={removeFromCart}
                                        onAddAccessory={handleAddAccessory}
                                        onAddAccessoryToViewerOnly={(accessory) => handleAddAccessory(accessory, { addToCart: false })}
                                        onRemoveAccessory={handleRemoveAccessory}
                                        onRemoveAccessoryFromViewerOnly={(code) => handleRemoveAccessory(code, { removeFromCart: false })}
                                        onShowAccessoryDetails={handleShowAccessoryDetails}
                                        categoryIdForCart={selectedCategory?.id}
                                        isRacingCategory={!!(racingCategoryId && selectedCategory && String(selectedCategory.id) === String(racingCategoryId))}
                                        racingCategoryId={racingCategoryId}
                                    />
                                )}
                            </>
                        )}
                    </div>

                    {/* Price disclaimer - above cart/checkout, after accessories list */}
                    <p className="personalize-panel-disclaimer">
                        <span language="ar">*السعر أعلاه يشمل العمالة وأي ضرائب تطبق. الأسعار قابلة للتغيير، يرجى التحقق من الأسعار النهائية مع الموزع.</span>
                        <span language="en">*Price above includes labor and any applicable taxes. Prices are subject to change, please verify final prices with Distributor.</span>
                    </p>

                    {/* Checkout and cart section - bottom of accessory sidebar */}
                    <div className="personalize-footer">
                        <div 
                            className="cart-icon-wrapper" 
                            onClick={() => {
                                pushViewerEvent('viewer_cart_icon_clicked');
                                window.location.href = window.location.origin + '/cart';
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-cart3" viewBox="0 0 16 16">
                                <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l.84 4.479 9.144-.459L13.89 4zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                            </svg>
                            {(() => {
                                const totalQuantity = cart ? Object.values(cart).reduce((acc, item) => acc + (item.quantity || 0), 0) : 0;
                                return totalQuantity > 0 ? (
                                    <span className="cart-badge">{totalQuantity}</span>
                                ) : null;
                            })()}
                        </div>
                        <button 
                            className="btn-checkout"
                            onClick={() => {
                                pushViewerEvent('viewer_checkout_clicked');
                                window.location.href = window.location.origin + '/checkout';
                            }}
                        >
                            <span language="ar">انتقل إلى السداد مع الخروج</span>
                            <span language="en">Proceed to Checkout</span>
                        </button>
                    </div>
                    </div>
                )}
            </div>

            {/* Compatibility Confirmation Modal */}
            {compatibilityModal && (
                <div
                    className="compatibility-modal-overlay"
                    onClick={() => {
                        pushViewerEvent('viewer_compatibility_modal_cancelled', {
                            modal_type: compatibilityModal.type,
                            accessory_id: compatibilityModal.accessory?.id,
                        });
                        compatibilityModal.onCancel();
                    }}
                >
                    <div className="compatibility-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="compatibility-modal-header">
                            <h2>
                                <span language="ar">يجب إجراء تغييرات</span>
                                <span language="en">Changes required</span>
                            </h2>
                            <button
                                type="button"
                                className="compatibility-modal-close"
                                onClick={() => {
                                    pushViewerEvent('viewer_compatibility_modal_cancelled', {
                                        modal_type: compatibilityModal.type,
                                        accessory_id: compatibilityModal.accessory?.id,
                                    });
                                    compatibilityModal.onCancel();
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                                </svg>
                            </button>
                        </div>
                        <div className="compatibility-modal-content">
                            {/* Conflicts */}
                            {compatibilityModal.type === 'conflict' && compatibilityModal.conflicts && compatibilityModal.conflicts.length > 0 && (
                                <>
                                    <div className="compatibility-info-banner">
                                        <span className="compatibility-info-icon" aria-hidden="true">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="12" y1="16" x2="12" y2="12"/>
                                                <line x1="12" y1="8" x2="12.01" y2="8"/>
                                            </svg>
                                        </span>
                                        <span>
                                            <span language="ar">سوف تؤدي إضافة <strong>{compatibilityModal.accessory.name}</strong> إلى إزالة الإكسسوارات أدناه</span>
                                            <span language="en">Adding <strong>{compatibilityModal.accessory.name}</strong> removes the accessories below</span>
                                        </span>
                                    </div>
                                    <div className="compatibility-accessories-list">
                                        {compatibilityModal.conflicts.map((conflict, idx) => {
                                            const conflictAccessory = conflict.accessory || availableAccessories.find(a => a.id === conflict.accessory_id);
                                            if (!conflictAccessory) return null;
                                            const displayName = conflictAccessory.accessory_name || conflictAccessory.name || conflictAccessory.part_number || 'Accessory';
                                            const mediaArr = Array.isArray(conflictAccessory.media) ? conflictAccessory.media : [];
                                            const thumbMedia = mediaArr.find(m => m.collection_name === 'print-image') || mediaArr[0];
                                            const thumbUrl = conflictAccessory.thumbnail || (thumbMedia && thumbMedia.original_url) || '/images/default.jpg';
                                            return (
                                                <div key={idx} className="compatibility-accessory-card">
                                                    <img src={thumbUrl} alt={displayName} onError={(e) => { e.target.src = '/images/default.jpg'; }} />
                                                    <div className="compatibility-accessory-details">
                                                        <div className="compatibility-accessory-header">
                                                            <h3>{displayName}</h3>
                                                            {((conflictAccessory.totalWithTax ?? conflictAccessory.price) != null) && (
                                                                <span className="compatibility-price">
                                                                    <span className="compatibility-price-label">
                                                                        <span language="ar">السعر</span>
                                                                        <span language="en">Fitted Price</span>
                                                                        *:{' '}
                                                                    </span>
                                                                    <span className="compatibility-price-value">
                                                                        {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(conflictAccessory.totalWithTax ?? conflictAccessory.price))} {conflictAccessory.currency || conflictAccessory.currency_code || compatibilityModal.accessory?.currency || compatibilityModal.accessory?.currency_code || availableAccessories[0]?.currency || ''}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {conflictAccessory.has_3d && (
                                                            <div className="compatibility-card-3d-badge">
                                                                <img src="/images/3d-icon-128x.png" alt="3D" />
                                                                <span>3D</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            
                            {/* Replacements */}
                            {compatibilityModal.type === 'replacement' && compatibilityModal.replacements && compatibilityModal.replacements.length > 0 && (
                                <>
                                    <div className="compatibility-info-banner">
                                        <span className="compatibility-info-icon" aria-hidden="true">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="12" y1="16" x2="12" y2="12"/>
                                                <line x1="12" y1="8" x2="12.01" y2="8"/>
                                            </svg>
                                        </span>
                                        <span>
                                            <span language="ar">سوف تؤدي إضافة <strong>{compatibilityModal.accessory.name}</strong> إلى إزالة الإكسسوارات أدناه</span>
                                            <span language="en">Adding <strong>{compatibilityModal.accessory.name}</strong> removes the accessories below</span>
                                        </span>
                                    </div>
                                    <div className="compatibility-accessories-list">
                                        {compatibilityModal.replacements.map((replacement, idx) => {
                                            const replacementAccessory = availableAccessories.find(a => a.id === replacement.accessory_id);
                                            if (!replacementAccessory) return null;
                                            const displayName = replacementAccessory.name || replacementAccessory.part_number || 'Accessory';
                                            return (
                                                <div key={idx} className="compatibility-accessory-card">
                                                    <img src={replacementAccessory.thumbnail || replacementAccessory.media?.[0]?.original_url || '/images/default.jpg'} alt={displayName} onError={(e) => { e.target.src = '/images/default.jpg'; }} />
                                                    <div className="compatibility-accessory-details">
                                                        <div className="compatibility-accessory-header">
                                                            <h3>{displayName}</h3>
                                                            {((replacementAccessory.totalWithTax ?? replacementAccessory.price) != null) && (
                                                                <span className="compatibility-price">
                                                                    <span className="compatibility-price-label">
                                                                        <span language="ar">السعر</span>
                                                                        <span language="en">Fitted Price</span>
                                                                        *:{' '}
                                                                    </span>
                                                                    <span className="compatibility-price-value">
                                                                        {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(replacementAccessory.totalWithTax ?? replacementAccessory.price))} {replacementAccessory.currency || replacementAccessory.currency_code || compatibilityModal.accessory?.currency || compatibilityModal.accessory?.currency_code || availableAccessories[0]?.currency || ''}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {replacementAccessory.has_3d && (
                                                            <div className="compatibility-card-3d-badge">
                                                                <img src="/images/3d-icon-128x.png" alt="3D" />
                                                                <span>3D</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            {/* Dependencies */}
                            {compatibilityModal.type === 'dependency' && compatibilityModal.dependencies && compatibilityModal.dependencies.length > 0 && (
                                <>
                                    <div className="compatibility-info-banner">
                                        <span className="compatibility-info-icon" aria-hidden="true">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="12" y1="16" x2="12" y2="12"/>
                                                <line x1="12" y1="8" x2="12.01" y2="8"/>
                                            </svg>
                                        </span>
                                        <span>
                                            <span language="ar">سوف تؤدي إضافة <strong>{compatibilityModal.accessory.name}</strong> أيضًا إلى إضافة {compatibilityModal.dependencies?.length || 0} إكسسوارات أدناه</span>
                                            <span language="en">Adding <strong>{compatibilityModal.accessory.name}</strong> will also add the {compatibilityModal.dependencies?.length || 0} accessories below</span>
                                        </span>
                                    </div>
                                    <div className="compatibility-accessories-list">
                                        {compatibilityModal.dependencies.map((dependency, idx) => {
                                            const dependencyAccessory = availableAccessories.find(a => a.id === dependency.accessory_id);
                                            if (!dependencyAccessory) return null;
                                            const displayName = dependencyAccessory.name || dependencyAccessory.part_number || 'Accessory';
                                            return (
                                                <div key={idx} className="compatibility-accessory-card">
                                                    <img src={dependencyAccessory.thumbnail || dependencyAccessory.media?.[0]?.original_url || '/images/default.jpg'} alt={displayName} onError={(e) => { e.target.src = '/images/default.jpg'; }} />
                                                    <div className="compatibility-accessory-details">
                                                        <div className="compatibility-accessory-header">
                                                            <h3>{displayName}</h3>
                                                            {((dependencyAccessory.totalWithTax ?? dependencyAccessory.price) != null) && (
                                                                <span className="compatibility-price">
                                                                    <span className="compatibility-price-label">
                                                                        <span language="ar">السعر</span>
                                                                        <span language="en">Fitted Price</span>
                                                                        *:{' '}
                                                                    </span>
                                                                    <span className="compatibility-price-value">
                                                                        {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(dependencyAccessory.totalWithTax ?? dependencyAccessory.price))} {dependencyAccessory.currency || dependencyAccessory.currency_code || compatibilityModal.accessory?.currency || compatibilityModal.accessory?.currency_code || availableAccessories[0]?.currency || ''}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {dependencyAccessory.has_3d && (
                                                            <div className="compatibility-card-3d-badge">
                                                                <img src="/images/3d-icon-128x.png" alt="3D" />
                                                                <span>3D</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            <div className="compatibility-modal-question">
                                <span language="ar">هل ترغب في الاستمرار؟</span>
                                <span language="en">Do you want to continue?</span>
                            </div>
                            <div className="compatibility-modal-actions">
                                <button
                                    type="button"
                                    className="btn-yes"
                                    onClick={() => {
                                        pushViewerEvent('viewer_compatibility_modal_confirmed', {
                                            modal_type: compatibilityModal.type,
                                            accessory_id: compatibilityModal.accessory?.id,
                                        });
                                        compatibilityModal.onConfirm();
                                    }}
                                >
                                    <span language="ar">نعم</span>
                                    <span language="en">Yes</span>
                                </button>
                                <button
                                    type="button"
                                    className="btn-no"
                                    onClick={() => {
                                        pushViewerEvent('viewer_compatibility_modal_cancelled', {
                                            modal_type: compatibilityModal.type,
                                            accessory_id: compatibilityModal.accessory?.id,
                                        });
                                        compatibilityModal.onCancel();
                                    }}
                                >
                                    <span language="ar">لا</span>
                                    <span language="en">No</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Accessory Detail Flyout */}
            {accessoryDetailFlyout && (
                <div className="accessory-detail-flyout-overlay" onClick={closeAccessoryDetailFlyout}>
                    <div className="accessory-detail-flyout" onClick={(e) => e.stopPropagation()}>
                        <button 
                            type="button"
                            className="accessory-detail-close" 
                            onClick={closeAccessoryDetailFlyout}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                            </svg>
                        </button>
                        {accessoryDetailFlyout.loading ? (
                            <div className="accessory-detail-loading">
                                <span language="ar">جاري التحميل...</span>
                                <span language="en">Loading...</span>
                            </div>
                        ) : accessoryDetailFlyout.accessory ? (
                            <>
                                <h2 className="accessory-detail-title">{accessoryDetailFlyout.accessory.accessory_name || accessoryDetailFlyout.accessory.name}</h2>
                                <p className="accessory-detail-part-number">
                                    <span language="ar">رقم الجزء</span>
                                    <span language="en">Part Number</span>
                                    : {accessoryDetailFlyout.accessory.part_number}
                                </p>
                                <div className="accessory-detail-image">
                                    {accessoryDetailFlyout.accessory.media && accessoryDetailFlyout.accessory.media.find(m => m.collection_name === "print-image") ? (
                                        <img 
                                            src={accessoryDetailFlyout.accessory.media.find(m => m.collection_name === "print-image").original_url} 
                                            alt={accessoryDetailFlyout.accessory.accessory_name || accessoryDetailFlyout.accessory.name}
                                            onError={(e) => { e.target.src = accessoryDetailFlyout.accessory.thumbnail || '/images/default.jpg'; }}
                                        />
                                    ) : (
                                        <img 
                                            src={accessoryDetailFlyout.accessory.thumbnail || '/images/default.jpg'} 
                                            alt={accessoryDetailFlyout.accessory.accessory_name || accessoryDetailFlyout.accessory.name}
                                            onError={(e) => { e.target.src = '/images/default.jpg'; }}
                                        />
                                    )}
                                </div>
                                <div className="accessory-detail-price">
                                    <p className="accessory-detail-price-label">
                                        <span language="ar">السعر</span>
                                        <span language="en">Fitted Price</span>
                                        *
                                    </p>
                                    <p className="accessory-detail-price-value">
                                        {(() => {
                                            const acc = accessoryDetailFlyout.accessory;
                                            const fittedPrice = acc.totalWithTax ?? acc.price;
                                            if (fittedPrice == null) return null;
                                            return (
                                                <>{new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(fittedPrice))} {acc.currency}</>
                                            );
                                        })()}
                                    </p>
                                </div>
                                {(() => {
                                    const acc = accessoryDetailFlyout.accessory;
                                    const listAcc = availableAccessories.find((a) => String(a.id) === String(acc.id));
                                    const mergedAcc = {
                                        ...acc,
                                        ...(listAcc || {}),
                                        one3d_item_code: acc.one3d_item_code || listAcc?.one3d_item_code,
                                        has_3d: acc.has_3d ?? listAcc?.has_3d,
                                    };
                                    const inCart = cart && cart[acc.id];
                                    const on3dCanvas = mergedAcc.one3d_item_code && selectedAccessories.includes(mergedAcc.one3d_item_code);
                                    if (inCart) {
                                        return (
                                            <button 
                                                type="button"
                                                className="accessory-detail-cta-btn accessory-detail-remove-btn"
                                                disabled={isUpdatingAccessories}
                                                onClick={() => {
                                                    if (on3dCanvas && mergedAcc.one3d_item_code) {
                                                        handleRemoveAccessory(mergedAcc.one3d_item_code);
                                                    } else if (removeFromCart && typeof removeFromCart === 'function') {
                                                        removeFromCart(acc.id);
                                                    }
                                                }}
                                            >
                                                <span language="ar">إزالة</span>
                                                <span language="en">Remove</span>
                                            </button>
                                        );
                                    }
                                    return (
                                        <button 
                                            type="button"
                                            className="accessory-detail-cta-btn accessory-detail-add-btn"
                                            disabled={isUpdatingAccessories}
                                            onClick={() => {
                                                if (mergedAcc.one3d_item_code && !selectedAccessories.includes(mergedAcc.one3d_item_code)) {
                                                    handleAddAccessory(mergedAcc);
                                                } else if (addToCart && typeof addToCart === 'function') {
                                                    addToCart({
                                                        id: acc.id,
                                                        quantity: 1,
                                                        category_id: localStorage.getItem('category_id') || ''
                                                    }, 1);
                                                }
                                            }}
                                        >
                                            <span language="ar">إضافة</span>
                                            <span language="en">Add</span>
                                        </button>
                                    );
                                })()}
                                {accessoryDetailFlyout.accessory.description && (
                                    <div
                                        className={`accessory-detail-description${
                                            racingCategoryId && selectedCategory && String(selectedCategory.id) === String(racingCategoryId)
                                                ? ' accessory-detail-description--well'
                                                : ''
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: safeRichTextHtml(accessoryDetailFlyout.accessory.description) }}
                                    />
                                )}
                                <p className="accessory-detail-flyout-disclaimer">
                                    <span language="ar">*السعر أعلاه يشمل العمالة وأي ضرائب تطبق. الأسعار قابلة للتغيير، يرجى التحقق من الأسعار النهائية مع الموزع.</span>
                                    <span language="en">*Price above includes labor and any applicable taxes. Prices are subject to change, please verify final prices with Distributor.</span>
                                </p>
                            </>
                        ) : (
                            <div className="accessory-detail-error">
                                <span language="ar">حدث خطأ في تحميل التفاصيل</span>
                                <span language="en">Error loading details</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}

export default ThreeDViewer;
